/**
 * Shared band-per-task layout for the new experience builder. Both the main board
 * (spine-builder) and the overview map (builder-map-modal) derive their geometry from this one
 * function so they can never disagree about the shape of the flow graph. Dimensions are passed in
 * so the same topology can render at card scale on the board and at thumbnail scale on the map.
 */

export interface Placed { id: string; col: number; x: number; y: number }

export interface LayoutDims {
  cw: number;   // card width
  ch: number;   // card height
  vg: number;   // vertical gap between stacked cards
  cgap: number; // horizontal gap between redirect columns
}

export interface Layout {
  placed: Map<string, Placed>;
  width: number;
  height: number;
}

// Place a card, lay its detours (recursively) stacked in the next column, then advance below the
// taller of {card, its detour band} so nothing collides. `reserveTop(id)` adds a `headGap` band
// ABOVE a card — the board uses it so a branch's purpose label (rendered above its head) has room
// and doesn't collide with the End cap of the card stacked above it. The map passes neither.
function layoutSpine(
  ids: string[],
  col: number,
  top: number,
  detoursOf: Record<string, string[]>,
  placed: Map<string, Placed>,
  dims: LayoutDims,
  reserveTop: (id: string) => boolean,
  headGap: number,
  reserveBottom: (id: string) => boolean,
  tailGap: number,
  heightOf: (id: string) => number,
  gapAfter: (id: string) => number,
): number {
  let y = top;
  let lastGap = dims.vg;
  for (const id of ids) {
    if (reserveTop(id)) y += headGap;
    placed.set(id, { id, col, x: col * (dims.cw + dims.cgap), y });
    const detours = detoursOf[id] ?? [];
    const bandTop = y;
    let bandBottom = y + heightOf(id) + (reserveBottom(id) ? tailGap : 0); // room below for an End cap + New Task
    if (detours.length) {
      const detourBottom = layoutSpine(detours, col + 1, bandTop, detoursOf, placed, dims, reserveTop, headGap, reserveBottom, tailGap, heightOf, gapAfter);
      bandBottom = Math.max(bandBottom, detourBottom);
    }
    lastGap = gapAfter(id);
    y = bandBottom + lastGap;
  }
  return y - lastGap; // drop the trailing gap after the final item
}

export function computeLayout(
  spine: string[],
  detoursOf: Record<string, string[]>,
  dims: LayoutDims,
  opts?: { reserveTop?: (id: string) => boolean; headGap?: number; reserveBottom?: (id: string) => boolean; tailGap?: number; heightOf?: (id: string) => number; gapAfter?: (id: string) => number },
): Layout {
  const placed = new Map<string, Placed>();
  const heightOf = opts?.heightOf ?? (() => dims.ch);
  const gapAfter = opts?.gapAfter ?? (() => dims.vg);
  const reserveBottom = opts?.reserveBottom ?? (() => false);
  const tailGap = opts?.tailGap ?? 0;
  layoutSpine(spine, 0, 0, detoursOf, placed, dims, opts?.reserveTop ?? (() => false), opts?.headGap ?? 0, reserveBottom, tailGap, heightOf, gapAfter);
  let width = 0;
  let height = 0;
  for (const p of placed.values()) {
    width = Math.max(width, p.x + dims.cw);
    // Include a terminal's reserved tail (its End cap + New Task hang below the card) so the board's
    // content height + column envelopes actually contain them, instead of the button spilling out the
    // bottom. `layoutSpine`'s return value only sizes gaps BETWEEN stacked items, not the last one.
    height = Math.max(height, p.y + heightOf(p.id) + (reserveBottom(p.id) ? tailGap : 0));
  }
  return { placed, width: width + 20, height: height + 12 };
}
